/**
 * BidSubmissionsCard - Shows bid submission status for each GC on a job
 * Displayed in the Notes/Follow-Up tab for tracking when bids were sent.
 *
 * Supports resubmissions: if an addendum is issued after the original bid was
 * sent, users can record a new submission date/time while preserving the
 * original. All resubmissions are stored in a `resubmissions` array on each GC.
 */
import { useState } from "react";
import { Send, Clock, Edit2, RefreshCw, Trash2 } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr + (dateStr.length === 10 ? "T00:00:00" : ""));
  if (isNaN(date.getTime())) return dateStr;
  return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
    date.getDate()
  ).padStart(2, "0")}/${date.getFullYear()}`;
};

const BidSubmissionsCard = ({ job, onUpdateBiddingGCs, canEdit }) => {
  // Edit (original submission) state
  const [editingGcId, setEditingGcId] = useState(null);
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [editNotSent, setEditNotSent] = useState(false);

  // Resubmit dialog state
  const [resubmitGcId, setResubmitGcId] = useState(null);
  const [resubmitDate, setResubmitDate] = useState("");
  const [resubmitTime, setResubmitTime] = useState("");
  const [resubmitNote, setResubmitNote] = useState("");

  const biddingGCs = job?.bidding_gcs || [];
  if (biddingGCs.length === 0) return null;

  const contractedGcId = job?.contracted_gc_id;

  const handleEditClick = (gc) => {
    setEditingGcId(gc.gc_id);
    setEditDate(gc.bid_sent_date || "");
    setEditTime(gc.bid_sent_time || "");
    setEditNotSent(gc.bid_not_sent || false);
  };

  const handleSave = () => {
    const updated = biddingGCs.map((gc) => {
      if (gc.gc_id === editingGcId) {
        return {
          ...gc,
          bid_sent_date: editNotSent ? "" : editDate,
          bid_sent_time: editNotSent ? "" : editTime,
          bid_not_sent: editNotSent,
          // Clear resubmissions if marked not sent
          resubmissions: editNotSent ? [] : gc.resubmissions || [],
        };
      }
      return gc;
    });
    onUpdateBiddingGCs(updated);
    setEditingGcId(null);
  };

  const handleResubmitClick = (gc) => {
    const today = new Date().toISOString().slice(0, 10);
    setResubmitGcId(gc.gc_id);
    setResubmitDate(today);
    setResubmitTime("");
    setResubmitNote("");
  };

  const handleResubmitSave = () => {
    if (!resubmitDate) return;
    const updated = biddingGCs.map((gc) => {
      if (gc.gc_id === resubmitGcId) {
        const prior = Array.isArray(gc.resubmissions) ? gc.resubmissions : [];
        return {
          ...gc,
          resubmissions: [
            ...prior,
            {
              date: resubmitDate,
              time: resubmitTime,
              note: resubmitNote,
              created_at: new Date().toISOString(),
            },
          ],
        };
      }
      return gc;
    });
    onUpdateBiddingGCs(updated);
    setResubmitGcId(null);
  };

  const handleRemoveResubmission = (gcId, index) => {
    const updated = biddingGCs.map((gc) => {
      if (gc.gc_id === gcId) {
        const prior = Array.isArray(gc.resubmissions) ? gc.resubmissions : [];
        return {
          ...gc,
          resubmissions: prior.filter((_, i) => i !== index),
        };
      }
      return gc;
    });
    onUpdateBiddingGCs(updated);
  };

  const getStatusBadge = (gc) => {
    if (gc.gc_id === contractedGcId) {
      return (
        <Badge className="bg-green-100 text-green-700 border-green-200">
          Contracted
        </Badge>
      );
    }
    if (gc.bid_not_sent) {
      return (
        <Badge variant="outline" className="text-muted-foreground">
          Not Bidding
        </Badge>
      );
    }
    if (gc.bid_sent_date) {
      const resubCount = (gc.resubmissions || []).length;
      if (resubCount > 0) {
        return (
          <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">
            Resubmitted {resubCount > 1 ? `×${resubCount}` : ""}
          </Badge>
        );
      }
      return (
        <Badge className="bg-blue-100 text-blue-700 border-blue-200">
          Sent
        </Badge>
      );
    }
    return (
      <Badge
        variant="outline"
        className="text-amber-600 border-amber-300 bg-amber-50"
      >
        Pending
      </Badge>
    );
  };

  const sentCount = biddingGCs.filter(
    (gc) => gc.bid_sent_date || gc.bid_not_sent
  ).length;
  const totalCount = biddingGCs.length;

  const resubmitGc = biddingGCs.find((gc) => gc.gc_id === resubmitGcId);

  return (
    <>
      <Card data-testid="bid-submissions-card">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Send className="w-4 h-4 text-primary" />
              <h3 className="font-semibold text-sm">Bid Submissions</h3>
              <span className="text-xs text-muted-foreground">
                ({sentCount}/{totalCount} complete)
              </span>
            </div>
          </div>

          <div className="space-y-2">
            {biddingGCs.map((gc) => {
              const resubmissions = Array.isArray(gc.resubmissions)
                ? gc.resubmissions
                : [];
              const canResubmit =
                canEdit &&
                !!gc.bid_sent_date &&
                !gc.bid_not_sent &&
                gc.gc_id !== contractedGcId;

              return (
                <div
                  key={gc.gc_id}
                  className="rounded-md border bg-card hover:bg-muted/30 transition-colors"
                  data-testid={`bid-submission-${gc.gc_id}`}
                >
                  {/* Top row: name + status + actions */}
                  <div className="flex items-start justify-between p-3 gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {gc.gc_name}
                      </p>
                      {gc.bid_sent_date && !gc.bid_not_sent && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          <Clock className="w-3 h-3" />
                          Sent {formatDisplayDate(gc.bid_sent_date)}
                          {gc.bid_sent_time && ` at ${gc.bid_sent_time}`}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {getStatusBadge(gc)}
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleEditClick(gc)}
                          data-testid={`edit-bid-submission-${gc.gc_id}`}
                          title="Edit original submission"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {canResubmit && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs gap-1"
                          onClick={() => handleResubmitClick(gc)}
                          data-testid={`resubmit-bid-${gc.gc_id}`}
                          title="Record an additional submission (e.g. after an addendum)"
                        >
                          <RefreshCw className="w-3 h-3" />
                          Resubmit
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Resubmissions history */}
                  {resubmissions.length > 0 && (
                    <div className="border-t bg-muted/20 px-3 py-2 space-y-1">
                      {resubmissions.map((r, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between text-xs"
                          data-testid={`resubmission-${gc.gc_id}-${idx}`}
                        >
                          <div className="flex items-center gap-1.5 text-muted-foreground min-w-0">
                            <RefreshCw className="w-3 h-3 text-indigo-600 shrink-0" />
                            <span className="font-medium text-foreground">
                              Resubmitted {formatDisplayDate(r.date)}
                              {r.time && ` at ${r.time}`}
                            </span>
                            {r.note && (
                              <span className="truncate">— {r.note}</span>
                            )}
                          </div>
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 shrink-0"
                              onClick={() =>
                                handleRemoveResubmission(gc.gc_id, idx)
                              }
                              data-testid={`delete-resubmission-${gc.gc_id}-${idx}`}
                              title="Remove this resubmission"
                            >
                              <Trash2 className="w-3 h-3 text-destructive" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Edit Original Submission Dialog */}
      <Dialog
        open={!!editingGcId}
        onOpenChange={(open) => !open && setEditingGcId(null)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Bid Submission</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm font-medium">
              {biddingGCs.find((gc) => gc.gc_id === editingGcId)?.gc_name}
            </p>

            <div className="flex items-center gap-2">
              <Checkbox
                id="bid-not-sent"
                checked={editNotSent}
                onCheckedChange={(checked) => setEditNotSent(checked)}
                data-testid="bid-not-sent-checkbox"
              />
              <Label htmlFor="bid-not-sent" className="text-sm cursor-pointer">
                Bid Not Sent (not bidding this GC)
              </Label>
            </div>

            {!editNotSent && (
              <>
                <div className="form-group">
                  <Label>Date Sent</Label>
                  <Input
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    data-testid="bid-sent-date-input"
                  />
                </div>
                <div className="form-group">
                  <Label>Time Sent</Label>
                  <Input
                    type="time"
                    value={editTime}
                    onChange={(e) => setEditTime(e.target.value)}
                    data-testid="bid-sent-time-input"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingGcId(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="save-bid-submission-btn">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resubmit Dialog */}
      <Dialog
        open={!!resubmitGcId}
        onOpenChange={(open) => !open && setResubmitGcId(null)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Record Resubmission</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <p className="text-sm font-medium">{resubmitGc?.gc_name}</p>
              {resubmitGc?.bid_sent_date && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Original sent {formatDisplayDate(resubmitGc.bid_sent_date)}
                  {resubmitGc.bid_sent_time &&
                    ` at ${resubmitGc.bid_sent_time}`}
                </p>
              )}
            </div>

            <div className="form-group">
              <Label>Resubmission Date</Label>
              <Input
                type="date"
                value={resubmitDate}
                onChange={(e) => setResubmitDate(e.target.value)}
                data-testid="resubmit-date-input"
              />
            </div>
            <div className="form-group">
              <Label>Resubmission Time</Label>
              <Input
                type="time"
                value={resubmitTime}
                onChange={(e) => setResubmitTime(e.target.value)}
                data-testid="resubmit-time-input"
              />
            </div>
            <div className="form-group">
              <Label>Note (optional)</Label>
              <Input
                type="text"
                placeholder="e.g. Addendum #2"
                value={resubmitNote}
                onChange={(e) => setResubmitNote(e.target.value)}
                data-testid="resubmit-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResubmitGcId(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleResubmitSave}
              disabled={!resubmitDate}
              data-testid="save-resubmit-btn"
            >
              Save Resubmission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BidSubmissionsCard;
